import { Func } from 'interfaces/func';
import SurveyStatisticsQuestionAnswer from 'models/statistics/survey/questions/answer';
import SurveyStatisticsChoiceQuestionAnswer from 'models/statistics/survey/questions/choiceAnswer';
import SurveyStatisticsChoiceQuestion from 'models/statistics/survey/questions/choiceQuestion';
import SurveyStatisticsQuestion from 'models/statistics/survey/questions/question';
import SurveyStatisticsRankQuestionAnswer from 'models/statistics/survey/questions/rankAnswer';
import SurveyStatisticsRankQuestion from 'models/statistics/survey/questions/rankQuestion';
import SurveyQuestion from 'models/survey/question';
import SurveyStatisticsQuestionSection from '../models/questionSection';
import SurveyStatisticsBlankQuestion from "../../../../models/statistics/survey/questions/blankQuestion";

function roundToOneDecimal(number: number) {
    return Math.round(number * 10) / 10;
}

function reduceAnswers<T extends SurveyStatisticsQuestionAnswer>(answers: Array<T>, reducer: Func<number, [T]>) {
    return answers.reduce((result, answer) => {
        result[answer.questionChoiceId()] = reducer(answer);

        return result;
    }, <StringMap<number>>{});
}

export default class SurveyStatisticsQuestionSectionsFactory {
    static instance = new SurveyStatisticsQuestionSectionsFactory();

    constructor() { }

    buildSections(question: SurveyQuestion, statQuestion: SurveyStatisticsQuestion): Array<SurveyStatisticsQuestionSection> {
        if (statQuestion instanceof SurveyStatisticsChoiceQuestion)
            return this.buildChoiceSections(question, statQuestion);
        else if (statQuestion instanceof SurveyStatisticsBlankQuestion)
            return this.buildBlankSections(question, statQuestion);
        else if (statQuestion instanceof SurveyStatisticsRankQuestion)
            return this.buildRankSections(question, statQuestion);
        else
            return [];
    }

    private buildChoiceSections(question: SurveyQuestion, statQuestion: SurveyStatisticsChoiceQuestion) {
        return [
            new SurveyStatisticsQuestionSection({
                surveyQuestion: question,
                surveyStatQuestion: statQuestion,

                value: answers => reduceAnswers(answers, a => a.value()),

                rank: answers => {
                    const respondentsCount = statQuestion.respondentsCount();
                    const totalVotes = (respondentsCount || 1) / 100;

                    return answers.reduce((result, answer) => {
                        result[answer.questionChoiceId()] = Math.round(answer.value() / totalVotes * 10) / 10;

                        return result;
                    }, <StringMap<number>>{});
                },

                sortBy: this.getChoicesSortFunc(question, statQuestion)
            })
        ]
    }

    private buildBlankSections(question: SurveyQuestion, statQuestion: SurveyStatisticsBlankQuestion) {
        return [
            new SurveyStatisticsQuestionSection({
                surveyQuestion: question,
                surveyStatQuestion: statQuestion,

                value: answers => reduceAnswers(answers, a => a.value()),

                rank: answers => {
                    const respondentsCount = statQuestion.respondentsCount();
                    const totalVotes = (respondentsCount || 1) / 100;

                    return answers.reduce((result, answer) => {
                        result[answer.blankOptionId()] = Math.round(answer.value() / totalVotes * 10) / 10;

                        return result;
                    }, <StringMap<number>>{});
                },

                sortBy: answer => question.blankOptions().find(o => o.optionId() == answer.questionChoiceId())?.sortOrder() ?? 0
            })
        ]
    }

    private buildRankSections(question: SurveyQuestion, statQuestion: SurveyStatisticsRankQuestion) {
        return [
            new SurveyStatisticsQuestionSection<SurveyStatisticsRankQuestionAnswer>({
                title: labels.AverageRank,
                scoremode: true,

                surveyQuestion: question,
                surveyStatQuestion: statQuestion,

                value: answers => {
                    return _.chain(answers)
                        .groupBy(answer => roundToOneDecimal(answer.average()))
                        .sortBy((g, k) => parseFloat(k))
                        .reduce((result, group) => {
                            group.forEach(answer => {
                                result.ranks[answer.questionChoiceId()] = result.currentRank;
                            });

                            result.currentRank += group.length;

                            return result;
                        }, { ranks: <StringMap<number>>{}, currentRank: 1 }).value().ranks;
                },

                rank: answers => reduceAnswers(answers, a => roundToOneDecimal(a.average())),

                sortBy: a => a.average()
            }),

            new SurveyStatisticsQuestionSection({
                title: labels.TimesRankedAsFirst,

                surveyQuestion: question,
                surveyStatQuestion: statQuestion,

                value: answers => reduceAnswers(answers, a => a.timesSelectedAsTop()),
                rank: answers => this.rankAnswersByField(answers, a => a.timesSelectedAsTop()),
                sortBy: a => -a.timesSelectedAsTop()
            }),

            new SurveyStatisticsQuestionSection({
                title: labels.TimesRankedAsLast,
                hidden: statQuestion.answers.all(a => a.timesSelectedAsLast() == undefined),

                surveyQuestion: question,
                surveyStatQuestion: statQuestion,

                value: answers => reduceAnswers(answers, a => a.timesSelectedAsLast() ?? 0),
                rank: answers => this.rankAnswersByField(answers, a => a.timesSelectedAsLast() ?? 0),
                sortBy: a => -(a.timesSelectedAsLast() ?? 0)
            })
        ];
    }

    private getChoicesSortFunc(question: SurveyQuestion, statQuestion: SurveyStatisticsChoiceQuestion): Func<string | number, [SurveyStatisticsChoiceQuestionAnswer]> {
        const choiceRatingType = question.choicesOptions.pluck(o => o.choiceRatingType);

        const getAnswerChoice = (answer: SurveyStatisticsChoiceQuestionAnswer) => question.choices().find(ch => ch.choiceId() == answer.questionChoiceId());
        const getAnswerTitle = (answer: SurveyStatisticsChoiceQuestionAnswer) => getAnswerChoice(answer)?.text()
        const getAnswerOrder = (answer: SurveyStatisticsChoiceQuestionAnswer) => getAnswerChoice(answer)?.order() ?? -1;

        if (choiceRatingType() == enums.ProfileQuestionChoicesRatingTypes.RatingScale.value || question.usesSlider())
            return a => getAnswerOrder(a);
        else if (statQuestion.isAllNumeric())
            return a => parseFloat(getAnswerTitle(a) ?? '')
        else
            return a => -a.value();
    }

    private rankAnswersByField(answers: Array<SurveyStatisticsRankQuestionAnswer>, extractValue: Func<number, [SurveyStatisticsRankQuestionAnswer]>) {
        var result = <StringMap<number>>{};

        if (answers.length > 0) {
            const total = answers.reduce((r, a) => r + extractValue(a), 0) || 1;

            let error = 100;

            result = answers.reduce((result, statAnswer) => {
                const rank = Math.round(extractValue(statAnswer) / total * 1000) / 10;

                result[statAnswer.questionChoiceId()] = rank;
                error -= rank;

                return result;
            }, <StringMap<number>>{});

            if (error < 100) {
                const choiceId = _(result).findKey(value => value > 0);

                if (choiceId != undefined)
                    result[choiceId] += error;
            }
        }

        return result;
    }
}